import React, { useState } from 'react';
import { accountsAPI } from '../services/api';

const FundingModal = ({ isOpen, onClose, defaultAmount }) => {
  const [amount, setAmount] = useState(defaultAmount || 50);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const presetAmounts = [25, 50, 100, 200, 500];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || amount < 1) {
      setError('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { data } = await accountsAPI.createCheckoutSession({ amount: parseFloat(amount) });
      // Hand off to Stripe's hosted Checkout page — it returns here via
      // success_url/cancel_url (see AccountPage's funding=success handling).
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout session error:', err);
      setError(err.response?.data?.error || 'Could not start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        maxWidth: '32rem',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>

        {/* Modal Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#111827'
          }}>
            Add Funds
          </h2>
          <button
            onClick={onClose}
            style={{
              fontSize: '1.25rem',
              color: '#6b7280',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.color = '#374151'}
            onMouseOut={(e) => e.target.style.color = '#6b7280'}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Select Amount
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
              gap: '0.5rem',
              marginBottom: '0.75rem'
            }}>
              {presetAmounts.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${amount === amt ? '#3b82f6' : '#d1d5db'}`,
                    background: amount === amt ? '#3b82f6' : '#ffffff',
                    color: amount === amt ? '#ffffff' : '#374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {formatCurrency(amt)}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0 0.75rem',
                fontSize: '0.875rem',
                color: '#111827',
                background: '#e5e7eb',
                border: '1px solid #d1d5db',
                borderRightWidth: 0,
                borderRadius: '0.375rem 0 0 0.375rem'
              }}>
                $
              </span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Math.max(1, parseFloat(e.target.value) || 0))}
                min="1"
                step="1"
                style={{
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0 0.375rem 0.375rem 0',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded border" style={{ marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={processing}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing ? 'Redirecting to Stripe…' : `Continue to pay ${formatCurrency(amount)}`}
            </button>

            <div className="text-xs text-gray-500 text-center mt-4">
              <p>You'll be redirected to Stripe to complete payment securely.</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FundingModal;
